import React, { useEffect, useState } from 'react';
import UserDashboardShell from '../../components/user/UserDashboardShell';
import { useAuth } from '../../auth/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import {
  createDefaultAddresses,
  readUserDashboardState,
  writeUserDashboardState,
} from '../../utils/userDashboardState';

const TrashIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M5 7h14" />
    <path d="M9 7V4.5h6V7" />
    <path d="M8 7v11a1.5 1.5 0 0 0 1.5 1.5h5A1.5 1.5 0 0 0 16 18V7" />
    <path d="M10 10.5v5" />
    <path d="M14 10.5v5" />
  </svg>
);

const PinIcon = () => (
  <svg viewBox="0 0 32 32" aria-hidden="true">
    <path d="M16 28s8-7.5 8-14A8 8 0 1 0 8 14c0 6.5 8 14 8 14Z" />
    <circle cx="16" cy="14" r="2.8" />
  </svg>
);

const INITIAL_ADDRESS_FORM = {
  street: '',
  region: '',
  note: '',
};

const DEFAULT_MAP_QUERY = 'Nutsubidze Plateau, Tbilisi, Georgia';

const buildMapQuery = ({ street, region }) => {
  const queryParts = [street.trim(), region.trim()].filter(Boolean);

  return queryParts.length > 0 ? queryParts.join(', ') : DEFAULT_MAP_QUERY;
};

const buildMapSrc = (formData) => {
  const query = encodeURIComponent(buildMapQuery(formData));

  return `https://maps.google.com/maps?q=${query}&z=13&output=embed`;
};

const AddressForm = ({ formData, onChange, onSubmit }) => {
  const { t } = useLanguage();

  return (
    <form className="account-entry-form account-entry-form--address" onSubmit={onSubmit}>
      <h2 className="account-entry-form__title">{t('addresses.addTitle')}</h2>

      <label className="account-entry-form__field">
      <span className="sr-only">{t('addresses.street')}</span>
      <input
        type="text"
        value={formData.street}
        placeholder={t('addresses.addStreet')}
        autoComplete="street-address"
        required
        onChange={(event) => onChange({ ...formData, street: event.target.value })}
      />
    </label>

      <label className="account-entry-form__field">
      <span className="sr-only">{t('addresses.region')}</span>
      <input
        type="text"
        value={formData.region}
        placeholder={t('addresses.region')}
        autoComplete="address-level1"
        required
        onChange={(event) => onChange({ ...formData, region: event.target.value })}
      />
    </label>

    <div className="account-entry-form__map">
      <iframe
        title={t('addresses.mapTitle')}
        src={buildMapSrc(formData)}
        loading="lazy"
        referrerPolicy="no-referrer-when-downgrade"
        allowFullScreen
      />
    </div>

    <p className="account-entry-form__map-label">{t('addresses.refineMap')}</p>

    <label className="account-entry-form__field account-entry-form__field--note">
      <span className="sr-only">{t('addresses.note')}</span>
      <textarea
        value={formData.note}
        placeholder={t('addresses.makeNote')}
        onChange={(event) => onChange({ ...formData, note: event.target.value })}
      />
    </label>

    <div className="account-entry-form__actions account-entry-form__actions--end">
      <button type="submit" className="account-button account-button--primary account-entry-form__next">
        {t('addresses.nextStep')}
      </button>
    </div>
  </form>
  );
};

const DeliveryAddresses = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [addresses, setAddresses] = useState([]);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [isAddingAddress, setIsAddingAddress] = useState(false);
  const [addressForm, setAddressForm] = useState(INITIAL_ADDRESS_FORM);

  useEffect(() => {
    if (!user?.id) {
      setAddresses([]);
      setHasLoaded(false);
      return;
    }

    const storedAddresses = readUserDashboardState('delivery-addresses', user.id, createDefaultAddresses());
    setAddresses(storedAddresses);
    setHasLoaded(true);
  }, [user?.id]);

  useEffect(() => {
    if (!hasLoaded || !user?.id) {
      return;
    }

    writeUserDashboardState('delivery-addresses', user.id, addresses);
  }, [addresses, hasLoaded, user?.id]);

  const handleDelete = (addressId) => {
    setAddresses((currentAddresses) => currentAddresses.filter((address) => address.id !== addressId));
  };

  const handleAddAddress = (event) => {
    event.preventDefault();

    const street = addressForm.street.trim();
    const region = addressForm.region.trim();

    if (!street || !region) {
      return;
    }

    setAddresses((currentAddresses) => [
      ...currentAddresses,
      {
        id: `delivery-address-${Date.now()}`,
        label: `${street}, ${region}.`,
        note: addressForm.note.trim(),
      },
    ]);
    setAddressForm(INITIAL_ADDRESS_FORM);
    setIsAddingAddress(false);
  };

  return (
    <UserDashboardShell>
      <section className="account-page">
        <h2 className="account-page__title">{t('addresses.title')}</h2>

        <div className="account-addresses__list">
          {addresses.map((address) => (
            <div key={address.id} className="account-addresses__item">
              <div className="account-addresses__card">
                <span className="account-addresses__pin">
                  <PinIcon />
                </span>
                <span className="account-addresses__text">{address.label}</span>
              </div>

              <button type="button" className="account-delete" onClick={() => handleDelete(address.id)} aria-label="Delete delivery address">
                <TrashIcon />
              </button>
            </div>
          ))}
        </div>

        {isAddingAddress ? (
          <AddressForm
            formData={addressForm}
            onChange={setAddressForm}
            onSubmit={handleAddAddress}
          />
        ) : (
          <button type="button" className="account-button account-button--outline" onClick={() => setIsAddingAddress(true)}>
            {t('addresses.addNew')}
          </button>
        )}
      </section>
    </UserDashboardShell>
  );
};

export default DeliveryAddresses;
